"use client";
import { useCallback, useSyncExternalStore } from "react";
import { requestJson } from "@/lib/clientRequest";

const INTERVAL = 30_000;

type Snapshot = { count: number | null };
const UNKNOWN: Snapshot = { count: null };

/**
 * One polling loop per attention URL, shared by every badge that shows it:
 * the tab strip, the rail row and the mode switcher on one page ask for the
 * same scope, and the server should hear about it once per interval, once
 * per activity refresh. The loop starts with the first subscriber and stops,
 * aborting any request in flight, when the last one leaves.
 */
type Feed = {
  subscribers: Set<() => void>;
  snapshot: Snapshot;
  request: AbortController | null;
  refresh: () => void;
  stop: () => void;
};
const feeds = new Map<string, Feed>();

function startFeed(url: string): Feed {
  const feed: Feed = { subscribers: new Set(), snapshot: UNKNOWN, request: null, refresh, stop };
  function publish(snapshot: Snapshot) {
    feed.snapshot = snapshot;
    for (const notify of feed.subscribers) notify();
  }
  async function load(replacePending: boolean) {
    if (feed.request && !replacePending) return;
    // A refresh follows a mutation: an older GET must not win over it.
    feed.request?.abort();
    const controller = new AbortController();
    feed.request = controller;
    try {
      const data = await requestJson<{ count?: unknown }>(url, {
        signal: controller.signal,
        fallback: "Could not load the inbox count.",
      });
      if (!controller.signal.aborted) publish({ count: typeof data?.count === "number" ? data.count : null });
    } catch {
      if (!controller.signal.aborted) publish(UNKNOWN);
    } finally {
      if (feed.request === controller) feed.request = null;
    }
  }
  function refresh() {
    void load(true);
  }
  const poll = () => {
    if (!document.hidden) void load(false);
  };
  const initial = setTimeout(poll, 0);
  const ticker = setInterval(poll, INTERVAL);
  document.addEventListener("visibilitychange", poll);
  window.addEventListener("caw:activity-refresh", refresh);
  function stop() {
    clearTimeout(initial);
    clearInterval(ticker);
    document.removeEventListener("visibilitychange", poll);
    window.removeEventListener("caw:activity-refresh", refresh);
    feed.request?.abort();
    feed.request = null;
    feeds.delete(url);
  }
  feeds.set(url, feed);
  return feed;
}

function subscribe(url: string, notify: () => void) {
  const feed = feeds.get(url) ?? startFeed(url);
  feed.subscribers.add(notify);
  return () => {
    feed.subscribers.delete(notify);
    if (feed.subscribers.size === 0) feed.stop();
  };
}

const readServer = () => UNKNOWN;

/**
 * How many items wait in the Inbox for a department, or for the whole
 * business when `departmentId` is null: the `count` of the attention feed,
 * which includes the operational incidents the Inbox lists beside its
 * decisions. Polled every 30 s and again after every `caw:activity-refresh`.
 * The count is null until the feed has loaded and whenever it failed: a
 * missing or failed feed must never read as zero. Nothing is requested while
 * `enabled` is false, for a badge nobody can see.
 */
export function useInboxCount(departmentId: string | null, enabled = true) {
  const url = enabled ? `/api/monitoring/attention${departmentId ? `?dept=${encodeURIComponent(departmentId)}` : ""}` : null;
  const subscribeTo = useCallback((notify: () => void) => (url ? subscribe(url, notify) : () => {}), [url]);
  const read = useCallback(() => (url ? feeds.get(url)?.snapshot ?? UNKNOWN : UNKNOWN), [url]);
  const { count } = useSyncExternalStore(subscribeTo, read, readServer);
  const refresh = useCallback(() => {
    if (url) feeds.get(url)?.refresh();
  }, [url]);
  return { count, refresh };
}
